import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiTrash2 } from 'react-icons/fi';
import {
  deleteVpsHostingRequest,
  getVpsHostingRequestById,
} from '../api/vpsHostingRequestsApi';
import ConfirmDialog from '../components/dashboard/ConfirmDialog';

const formatDate = (value) => {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleString();
};

const DetailRow = ({ label, value }) => (
  <div className="border-b border-[#f0e8dc] py-3 last:border-b-0">
    <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-[#9a9288]">{label}</p>
    <p className="mt-1 whitespace-pre-wrap text-sm text-[#130705]">{value || '—'}</p>
  </div>
);

const VpsHostingRequestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        const data = await getVpsHostingRequestById(id);
        if (!isMounted) {
          return;
        }

        setRequest(data);
        setLoadError('');
      } catch (err) {
        if (!isMounted) {
          return;
        }

        setRequest(null);
        setLoadError(err.message || 'Failed to load request.');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const handleDeleteClick = () => {
    setDeleteError('');
    setIsConfirmOpen(true);
  };

  const handleDeleteCancel = () => {
    if (isDeleting) {
      return;
    }

    setIsConfirmOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!request) {
      return;
    }

    setIsDeleting(true);
    setDeleteError('');

    try {
      await deleteVpsHostingRequest(request.id);
      navigate('/dashboard/vps-hosting-requests');
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete request.');
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return <p className="text-sm text-[#6b635c]">Loading request…</p>;
  }

  if (loadError) {
    return (
      <div className="space-y-4">
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {loadError}
        </p>
        <Link
          to="/dashboard/vps-hosting-requests"
          className="inline-flex text-sm font-medium text-[#785E32] hover:underline"
        >
          Back to requests
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl">
      <header className="mb-6 border-b border-[#e4d9c8] pb-5">
        <Link
          to="/dashboard/vps-hosting-requests"
          className="text-[12px] font-medium text-[#785E32] hover:underline"
        >
          ← All requests
        </Link>
        <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[12px] font-medium uppercase tracking-[0.16em] text-[#6b635c]">
              Request #{request.id}
            </p>
            <h2 className="mt-2 font-[Merriweather] text-[1.5rem] text-[#130705]">
              {request.fullName}
            </h2>
            <p className="mt-2 text-sm text-[#6b635c]">
              Submitted {formatDate(request.createdAt)}
            </p>
          </div>
          <button
            type="button"
            onClick={handleDeleteClick}
            disabled={isDeleting}
            className="inline-flex shrink-0 items-center gap-2 rounded-md !border !border-[#e4d9c8] !bg-white px-3 py-2 text-[13px] font-medium !text-[#6b635c] !shadow-none transition hover:!border-[#fecdca] hover:!bg-[#fef3f2] hover:!text-[#b42318] hover:!translate-y-0 hover:!shadow-none disabled:opacity-60"
          >
            <FiTrash2 className="h-4 w-4" aria-hidden="true" />
            {isDeleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
        {deleteError ? (
          <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {deleteError}
          </p>
        ) : null}
      </header>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-md border border-[#e4d9c8] bg-white p-5">
          <h3 className="mb-3 text-sm font-semibold text-[#130705]">Applicant details</h3>
          <DetailRow label="First name" value={request.firstName} />
          <DetailRow label="Last name" value={request.lastName} />
          <DetailRow label="Email" value={request.email} />
          <DetailRow
            label="Phone"
            value={`${request.countryCode} ${request.phone}`}
          />
          <DetailRow
            label="Privacy policy agreed"
            value={request.agreedToPrivacy ? 'Yes' : 'No'}
          />
        </section>

        <section className="rounded-md border border-[#e4d9c8] bg-white p-5">
          <h3 className="mb-3 text-sm font-semibold text-[#130705]">Server details</h3>
          <DetailRow label="Server location" value={request.serverLocation} />
          <DetailRow label="Server type" value={request.serverType} />
          <DetailRow label="Submitted" value={formatDate(request.createdAt)} />
        </section>
      </div>

      <section className="mt-6 rounded-md border border-[#e4d9c8] bg-white p-5">
        <h3 className="mb-3 text-sm font-semibold text-[#130705]">Message</h3>
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-[#130705]">
          {request.message}
        </p>
      </section>

      <ConfirmDialog
        isOpen={isConfirmOpen}
        title="Delete request?"
        message={`Delete the VPS request from ${request.fullName}? This cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={isDeleting}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default VpsHostingRequestDetailPage;
